import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environment';
import { ContentType } from '../model/cm/managed-content.model';
import { RequestState } from '../model/request-state.enum';
import { EnvService } from 'src/app/env-service/env.service';
import formElements from '../../../assets/demo/cm/formElements-eeoi.json';
import formSections from '../../../assets/demo/cm/formSections-eeoi.json';
import helpEEOI from '../../../assets/demo/cm/help-eeoi.json';
import tooltip from '../../../assets/demo/cm/tooltips-eeoi.json';
import questionsAnswersGroups from '../../../assets/demo/cm/questionAnswerGroups-eeoi.json';
import questionAnswers from '../../../assets/demo/cm/questionAnswers-eeoi.json';
import termsConsent from '../../../assets/demo/cm/termsconsent.json';
import { DRUPAL_ENVIRONMENT_MAPPING } from '../global-constants';

@Injectable({
  providedIn: 'root'
})
export class ManagedContentService {
  /**
   * Content types map is a map of section, elements, help, questions and question-groups maps.
   */
  public contentTypesMap: Map<string, Map<string, object>> = new Map();
  public contentTypesDefaultMap: Map<string, Map<string, object>> = new Map();
  private _contentReady$: BehaviorSubject<RequestState> = new BehaviorSubject(RequestState.PRISTINE);

  constructor(private http: HttpClient, private envService: EnvService) { }

  /**
   * Triggers initial call to get managed content. Returns an observable subscription so that the subscriber
   * would know when the calls finish
   */
  public loadManagedContent(): Observable<RequestState> {
    this._contentReady$.next(RequestState.WAITING);
    this.loadDefaultContent();
    this.loadCmContent();

    return this.contentReady;
  }

  /**
   * Loading default json drupal content, from files stored locally
   */
  private loadDefaultContent() {
    this.contentTypesDefaultMap.set(ContentType.ELEMENT, this.makeMapFromArray(formElements));
    this.contentTypesDefaultMap.set(ContentType.SECTION, this.makeMapFromArray(formSections));
    this.contentTypesDefaultMap.set(ContentType.MODAL, this.makeMapFromArray(termsConsent));
    this.contentTypesDefaultMap.set(ContentType.HELP, this.makeMapFromArray(helpEEOI));
    this.contentTypesDefaultMap.set(ContentType.QUESTION_GROUP, this.makeMapFromArray(questionsAnswersGroups));
    this.contentTypesDefaultMap.set(ContentType.QUESTION, this.makeMapFromArray(questionAnswers));
    this.contentTypesDefaultMap.set(ContentType.TOOLTIP, this.makeMapFromArray(tooltip));
  }


  private loadCmContent() {
  
    const deployPath = this.envService.deployPath;
    let drupalEnvironment = DRUPAL_ENVIRONMENT_MAPPING[this.envService.environment];

    if (environment == undefined)
    drupalEnvironment = DRUPAL_ENVIRONMENT_MAPPING.prod

    const sources = [
      this.managedContentCall(environment.getEnvironmentUrl(`https://cm.standard.com/form-elements/${drupalEnvironment}/eeoi/?_format=json`, deployPath)),
      this.managedContentCall(environment.getEnvironmentUrl(`https://cm.standard.com/form-sections/${drupalEnvironment}/eeoi/?_format=json`, deployPath)),
      this.managedContentCall(environment.getEnvironmentUrl(`https://cm.standard.com/dialog/v1/${drupalEnvironment}/eeoi/?_format=json`, deployPath)),
      this.managedContentCall(environment.getEnvironmentUrl(`https://cm.standard.com/help/${drupalEnvironment}/eeoi/?_format=json`, deployPath)),
      this.managedContentCall(environment.getEnvironmentUrl(`https://cm.standard.com/question-answer-groups/${drupalEnvironment}/eeoi/?_format=json`, deployPath)),
      this.managedContentCall(environment.getEnvironmentUrl(`https://cm.standard.com/question-answers/${drupalEnvironment}/eeoi/?_format=json`, deployPath)),
      this.managedContentCall(environment.getEnvironmentUrl(`https://cm.standard.com/tooltip/${drupalEnvironment}/eeoi/?_format=json`, deployPath))
    ];

    forkJoin(sources).subscribe(([elements, sections, terms, help, qaGrps, qa, tooltip]) => {
      if (!!elements) {
        this.contentTypesMap.set(ContentType.ELEMENT, this.makeMapFromArray(elements === null ?
          [] : Array.isArray(elements) ? elements : Object.values(elements)[0] as any[]));
      }
      if (!!sections) {
        this.contentTypesMap.set(ContentType.SECTION, this.makeMapFromArray(sections === null ?
          [] : Array.isArray(sections) ? sections : Object.values(sections)[0] as any[]));
      }
      if (!!terms) {
        this.contentTypesMap.set(ContentType.MODAL, this.makeMapFromArray(terms === null ?
          [] : Array.isArray(terms) ? terms : Object.values(terms)[0] as any[]));
      }
      if (!!help) {
        this.contentTypesMap.set(ContentType.HELP, this.makeMapFromArray(help === null ?
          [] : Array.isArray(help) ? help : Object.values(help)[0] as any[]));
      }
      if (!!qaGrps) {
        this.contentTypesMap.set(ContentType.QUESTION_GROUP, this.makeMapFromArray(qaGrps === null ?
          [] : Array.isArray(qaGrps) ? qaGrps : Object.values(qaGrps)[0] as any[]));
      }
      if (!!qa) {
        this.contentTypesMap.set(ContentType.QUESTION, this.makeMapFromArray(qa === null ?
          [] : Array.isArray(qa) ? qa : Object.values(qa)[0] as any[]));
      }
      if (!!tooltip) {
        this.contentTypesMap.set(ContentType.TOOLTIP, this.makeMapFromArray(tooltip === null ?
          [] : Array.isArray(tooltip) ? tooltip : Object.values(tooltip)[0] as any[]));
      }
    },
      error => {
        // TODO call route to something went wrong
        console.error('ERROR loading cm data: ', error);
        this._contentReady$.next(RequestState.FAIL);
      }
    );
    this._contentReady$.next(RequestState.SUCCESS);
  }

  get contentReady(): Observable<RequestState> {
    return this._contentReady$.asObservable();
  }

  /**
   * Get a single content object based on its id.
   * If the key doesn't exist, then an attempt will be made to replace it with default content.
   * @param contentType - specify this from the enum ContentTypes
   * @param id - the id of the single object you want
   */
  public getContentById(contentType: ContentType, id: string): any {
      return this.getContentItem(id, this.contentTypesMap.get(contentType), this.contentTypesDefaultMap.get(contentType));
  }

  /**
   * Get a map of content from a contentTypeMap based on a string to be found in the id..
   * @param contentType - specify this from the enum ContentTypes
   * @param idString - the portion of the id that will be used to identify the objects intended
   *        to be retrieved.
   */
  public getContentBySelection(contentType: ContentType, idString: string): any {
    return this.getSelection(idString, this.contentTypesMap.get(contentType));
  }

  //  Utilities  =========================================================
  /**
   * Utility to create a selected subset of a map based on a
   * string in the id
   */
  private getSelection(idString: string, inputMap: any): any {
    const outputMap = new Map();
    inputMap.forEach(item => {
      if (RegExp(idString).test(item.id)) {
        outputMap.set(item.id, item);
      }
    });
    return outputMap;
  }

  // Make a map from an Array based on "id" key
  private makeMapFromArray(inputArray: any[]): any {
    const outputMap = new Map();
    if (inputArray) {
      inputArray.forEach(item => {
        // Add and/or Modify properties
        // Adding properties for tooltip (id, title, plainText)
        // Stripping html and replacing entities on selected properties
        for (const [key, value] of Object.entries(item)) {
          switch(key){
            case 'tooltip-id':
              item.id = value;
            case 'tooltip-title':
              item.title = this.decodeHtml(String(value));
            case 'tooltip-plain-text':
              item.plainText = this.decodeHtml(String(value));
              break;
            case 'title':
              item.title = this.decodeHtml(String(value));
              break;
            case 'label':
              item.label = this.decodeHtml(String(value));
              break;
            case 'hint':
              item.hint = this.decodeHtml(String(value));
              break;
            case 'error':
              item.error = this.decodeHtml(String(value));
              break;
          }
        }
        outputMap.set(item.id, item);
      });
    }
    return outputMap;
  }

  /**
   * Get content item from a map, but use item from default content if the item is not found.
   * @param id - a key to the map currently being processed.
   * @param originalMap return an from this map by "Id" if it exists.
   * @param backupMap - this backup map will contain the possible missing object
   */
  private getContentItem(id: string, originalMap: Map<string, object>, backupMap: Map<string, object>): any {
    if (!!originalMap && !!originalMap.get(id)) {
      return originalMap.get(id);
    } else if (!!backupMap && !!backupMap.get(id)) {
      return backupMap.get(id);
    } else {
      return undefined;
    }
  }

  // TODO Make this private by removing other uses of it.
  public managedContentCall(endpoint: string): Observable<any> {
    return this.http.get<any>(endpoint);
  }

  // Removes tags and entities from a string
  public decodeHtml(html: string): string {
    let txtArea = document.createElement("textarea");
    txtArea.innerHTML = html;
    let txt = txtArea.value.replace(/<[^>]*>/g, '');
    return txt;
  }


}
